import { Button, Card } from "@sellar-io/components";
import { Alert } from "../../alert/Alert";
import { DialogNavigation, DialogContent, DialogCTA } from "../../modal/Dialog";
import { useFulfillmentTypeList } from "../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";

import { useNavigate } from "react-router";
import { DELIVERY_METHOD_CHARGES } from "../../../constants/routes";
import {
  formatCurrency,
  generateFulfillmentTypeContainerDeliveryChargeName,
} from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import {
  getFreeDeliveryBadge,
  getFlatChargeBadge,
} from "./PalletDeliverySettingsPage.shared";

export const PalletDeliverySettingsPage = ({
  onDismiss,
}: {
  onDismiss: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();

  const fulfillmentTypeHaulierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: business?.Supplier?.id ?? 0,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "haulier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!business?.Supplier?.id,
    },
  );

  const haulierFulfillmentType =
    fulfillmentTypeHaulierQuery.data?.pages[0]?.[0];

  const eligibilityGridCols = "grid-cols-[1fr_200px]";

  const hasCharges =
    haulierFulfillmentType?.FulfillmentTypeContainerDeliveryCharges &&
    haulierFulfillmentType.FulfillmentTypeContainerDeliveryCharges.length > 0;

  const charge = haulierFulfillmentType?.charge;
  const rule = haulierFulfillmentType?.DeliveryChargeRules[0];
  return (
    <>
      <DialogNavigation
        title="Update your pallet delivery settings"
        description="Check your pallet charges and free delivery offers are up to date. If not, select 'update my pallet settings' to update them."
      />
      <DialogContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 rounded-lg border p-4">
          <div className="flex items-center justify-between gap-4">
            <span className="font-semibold">
              Your current flat delivery charge
            </span>
            {getFlatChargeBadge(charge)}
          </div>
        </div>
        <div className="flex flex-col gap-2 rounded-lg border p-4">
          <div className="flex items-center justify-between gap-4">
            <span className="font-semibold">
              Your current free delivery offer
            </span>
            {getFreeDeliveryBadge(rule?.value)}
          </div>
          <Alert
            type="sellar"
            title="We recommend offering free delivery when customers spend £500 or more."
            message={
              "Encourage larger orders and make deliveries more efficient"
            }
            className="bg-zinc-50"
          />
        </div>
        {hasCharges ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-4">
              <span className="font-semibold">
                Your current delivery charges per item
              </span>
            </div>
            <Card className="border border-zinc-300 bg-white">
              <div
                className={twMerge(
                  "col-span-2 grid rounded-t-lg bg-zinc-50 font-semibold",
                  eligibilityGridCols,
                  !hasCharges && "rounded-b-lg",
                )}
              >
                <div
                  className={twMerge(
                    "rounded-tl-lg border-zinc-300 p-2",
                    hasCharges && "border-b",
                  )}
                >
                  Product format
                </div>
                <div
                  className={twMerge(
                    "border-zinc-300 p-2 text-right",
                    hasCharges && "border-b",
                  )}
                >
                  Delivery charge
                </div>
              </div>

              {haulierFulfillmentType.FulfillmentTypeContainerDeliveryCharges.map(
                (rule, idx) => (
                  <ChargeRuleRow
                    key={rule.id}
                    text={generateFulfillmentTypeContainerDeliveryChargeName({
                      volume: rule.volume,
                      packQuantity: rule.packQuantity,
                      displayUnit: rule.displayUnit,
                      containerType: rule.containerType,
                    })}
                    charge={rule.charge}
                    border={
                      idx + 1 <
                      haulierFulfillmentType
                        .FulfillmentTypeContainerDeliveryCharges.length
                    }
                    eligibilityGridCols={eligibilityGridCols}
                  />
                ),
              )}
            </Card>
          </div>
        ) : null}
      </DialogContent>
      <DialogCTA>
        <Button color="transparent" onClick={onDismiss}>
          I'm happy with my settings
        </Button>
        <Button
          onClick={() => {
            onDismiss();
            navigate(DELIVERY_METHOD_CHARGES.href({ method: "haulier" }));
          }}
        >
          Update my pallet settings
        </Button>
      </DialogCTA>
    </>
  );
};

const ChargeRuleRow = ({
  text,
  charge,
  eligibilityGridCols,
  border,
}: {
  text: string;
  charge: number;
  eligibilityGridCols: string;
  border: boolean;
}) => {
  return (
    <>
      <div className={twMerge("col-span-2 grid", eligibilityGridCols)}>
        <div className={twMerge("border-zinc-300 p-2", border && "border-b")}>
          {text}
        </div>
        <div
          className={twMerge(
            "border-zinc-300 p-2 text-right",
            border && "border-b",
          )}
        >
          {formatCurrency(charge)}
        </div>
      </div>
    </>
  );
};
